import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export interface TransientGateParameters {
  readonly fastAttackMs: number;
  readonly fastReleaseMs: number;
  readonly slowAttackMs: number;
  readonly slowReleaseMs: number;
  readonly attackGainDb: number;
  readonly sustainGainDb: number;
  readonly gateOpenDb: number;
  readonly gateHysteresisDb: number;
  readonly gateAttackMs: number;
  readonly gateReleaseMs: number;
  readonly outputGainDb: number;
}

export interface TransientShaperGateOptions extends Partial<TransientGateParameters> {
  readonly sampleRate: number;
}

type ParameterKey = keyof TransientGateParameters;
type ParameterId =
  | 'fast-attack-ms' | 'fast-release-ms' | 'slow-attack-ms' | 'slow-release-ms'
  | 'attack-gain-db' | 'sustain-gain-db' | 'gate-open-db' | 'gate-hysteresis-db'
  | 'gate-attack-ms' | 'gate-release-ms' | 'output-gain-db';

export const PARAMETER_BOUNDS = Object.freeze({
  fastAttackMs: Object.freeze({ min: 0, max: 20, unit: 'ms' }),
  fastReleaseMs: Object.freeze({ min: 1, max: 200, unit: 'ms' }),
  slowAttackMs: Object.freeze({ min: 1, max: 200, unit: 'ms' }),
  slowReleaseMs: Object.freeze({ min: 10, max: 2_000, unit: 'ms' }),
  attackGainDb: Object.freeze({ min: -24, max: 24, unit: 'dB' }),
  sustainGainDb: Object.freeze({ min: -24, max: 24, unit: 'dB' }),
  gateOpenDb: Object.freeze({ min: -80, max: 0, unit: 'dBFS' }),
  gateHysteresisDb: Object.freeze({ min: 0, max: 24, unit: 'dB' }),
  gateAttackMs: Object.freeze({ min: 0, max: 100, unit: 'ms' }),
  gateReleaseMs: Object.freeze({ min: 0, max: 2_000, unit: 'ms' }),
  outputGainDb: Object.freeze({ min: -24, max: 12, unit: 'dB' }),
} as const);

export const DEFAULT_TRANSIENT_GATE_PARAMETERS: Readonly<TransientGateParameters> = Object.freeze({
  fastAttackMs: 0.5,
  fastReleaseMs: 30,
  slowAttackMs: 20,
  slowReleaseMs: 180,
  attackGainDb: 6,
  sustainGainDb: 0,
  gateOpenDb: -48,
  gateHysteresisDb: 6,
  gateAttackMs: 3,
  gateReleaseMs: 80,
  outputGainDb: 0,
});

const ID_TO_KEY: Readonly<Record<ParameterId, ParameterKey>> = Object.freeze({
  'fast-attack-ms': 'fastAttackMs',
  'fast-release-ms': 'fastReleaseMs',
  'slow-attack-ms': 'slowAttackMs',
  'slow-release-ms': 'slowReleaseMs',
  'attack-gain-db': 'attackGainDb',
  'sustain-gain-db': 'sustainGainDb',
  'gate-open-db': 'gateOpenDb',
  'gate-hysteresis-db': 'gateHysteresisDb',
  'gate-attack-ms': 'gateAttackMs',
  'gate-release-ms': 'gateReleaseMs',
  'output-gain-db': 'outputGainDb',
});

const dbToGain = (value: number): number => 10 ** (value / 20);
const MAX_FLOAT32 = 3.4028234663852886e38;
const coefficient = (milliseconds: number, sampleRate: number): number => (
  milliseconds === 0 ? 0 : Math.exp(-1 / (milliseconds * 0.001 * sampleRate))
);

const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
};

/** Maximum instantaneous multiplier: +24 dB shape gain followed by +12 dB output gain. */
export const MAX_LINEAR_GAIN = dbToGain(
  Math.max(PARAMETER_BOUNDS.attackGainDb.max, PARAMETER_BOUNDS.sustainGainDb.max)
    + PARAMETER_BOUNDS.outputGainDb.max,
);

/**
 * Mono dual-envelope transient shaper with a hysteretic, smoothed gate.
 *
 * `processInto` performs no per-call or per-sample allocation. The processor is
 * causal and reports zero algorithmic latency; envelope and gain smoothing are
 * time-domain responses, not hidden delay/lookahead.
 */
export class TransientShaperGateProcessor {
  readonly sampleRate: number;
  private readonly values: TransientGateParameters;
  private fastEnvelope = 0;
  private slowEnvelope = 0;
  private gateOpen = false;
  private gateGain = 0;
  private shapeGain = 1;
  private smoothedOutputGain = 1;

  constructor(options: TransientShaperGateOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000);
    this.sampleRate = options.sampleRate;
    this.values = { ...DEFAULT_TRANSIENT_GATE_PARAMETERS, ...options };
    for (const key of Object.keys(PARAMETER_BOUNDS) as ParameterKey[]) {
      const bounds = PARAMETER_BOUNDS[key];
      assertRange(key, this.values[key], bounds.min, bounds.max);
    }
  }

  get latencySamples(): number { return 0; }
  get latencySeconds(): number { return 0; }
  get isGateOpen(): boolean { return this.gateOpen; }
  get currentGateGain(): number { return this.gateGain; }
  get currentFastEnvelope(): number { return this.fastEnvelope; }
  get currentSlowEnvelope(): number { return this.slowEnvelope; }

  reset(): void {
    this.fastEnvelope = 0;
    this.slowEnvelope = 0;
    this.gateOpen = false;
    this.gateGain = 0;
    this.shapeGain = 1;
    this.smoothedOutputGain = 1;
  }

  setParameter(id: string, value: number): void {
    const key = ID_TO_KEY[id as ParameterId];
    if (!key) throw new RangeError(`unknown parameter: ${id}`);
    const bounds = PARAMETER_BOUNDS[key];
    assertRange(id, value, bounds.min, bounds.max);
    (this.values as { -readonly [K in ParameterKey]: number })[key] = value;
  }

  processInto(
    input: Float32Array,
    output: Float32Array,
    transientEnvelope: Float32Array,
    sustainEnvelope: Float32Array,
    gateOutput: Float32Array,
    frames = output.length,
  ): void {
    if (!Number.isInteger(frames) || frames < 0
      || input.length < frames || output.length < frames
      || transientEnvelope.length < frames || sustainEnvelope.length < frames
      || gateOutput.length < frames) {
      throw new RangeError('buffers are too short for frames');
    }
    for (let frame = 0; frame < frames; frame += 1) {
      if (!Number.isFinite(input[frame]!)) throw new RangeError('input samples must be finite');
    }

    const fastAttack = coefficient(this.values.fastAttackMs, this.sampleRate);
    const fastRelease = coefficient(this.values.fastReleaseMs, this.sampleRate);
    const slowAttack = coefficient(this.values.slowAttackMs, this.sampleRate);
    const slowRelease = coefficient(this.values.slowReleaseMs, this.sampleRate);
    const gateAttack = coefficient(this.values.gateAttackMs, this.sampleRate);
    const gateRelease = coefficient(this.values.gateReleaseMs, this.sampleRate);
    const shapeSmoothing = coefficient(1, this.sampleRate);
    const openLevel = dbToGain(this.values.gateOpenDb);
    const closeLevel = dbToGain(this.values.gateOpenDb - this.values.gateHysteresisDb);
    const attackGain = dbToGain(this.values.attackGainDb);
    const sustainGain = dbToGain(this.values.sustainGainDb);
    const targetOutputGain = dbToGain(this.values.outputGainDb);

    let fastEnvelope = this.fastEnvelope;
    let slowEnvelope = this.slowEnvelope;
    let gateOpen = this.gateOpen;
    let gateGain = this.gateGain;
    let shapeGain = this.shapeGain;
    let smoothedOutputGain = this.smoothedOutputGain;

    for (let frame = 0; frame < frames; frame += 1) {
      const sample = input[frame]!;
      const level = Math.abs(sample);

      const fastCoefficient = level > fastEnvelope ? fastAttack : fastRelease;
      fastEnvelope = fastCoefficient * fastEnvelope + (1 - fastCoefficient) * level;
      const slowCoefficient = level > slowEnvelope ? slowAttack : slowRelease;
      slowEnvelope = slowCoefficient * slowEnvelope + (1 - slowCoefficient) * level;

      if (gateOpen) {
        if (fastEnvelope <= closeLevel) gateOpen = false;
      } else if (fastEnvelope >= openLevel) {
        gateOpen = true;
      }
      const gateTarget = gateOpen ? 1 : 0;
      const gateCoefficient = gateTarget > gateGain ? gateAttack : gateRelease;
      gateGain = gateCoefficient * gateGain + (1 - gateCoefficient) * gateTarget;

      const transient = Math.max(0, fastEnvelope - slowEnvelope);
      const transientShare = fastEnvelope > 1e-30 ? Math.min(1, transient / fastEnvelope) : 0;
      const targetShapeGain = sustainGain + transientShare * (attackGain - sustainGain);
      shapeGain = shapeSmoothing * shapeGain + (1 - shapeSmoothing) * targetShapeGain;
      smoothedOutputGain = shapeSmoothing * smoothedOutputGain
        + (1 - shapeSmoothing) * targetOutputGain;

      const shaped = sample * shapeGain * smoothedOutputGain * gateGain;
      output[frame] = Math.max(-MAX_FLOAT32, Math.min(MAX_FLOAT32, shaped));
      transientEnvelope[frame] = transient;
      sustainEnvelope[frame] = slowEnvelope;
      gateOutput[frame] = gateGain;
    }

    this.fastEnvelope = fastEnvelope;
    this.slowEnvelope = slowEnvelope;
    this.gateOpen = gateOpen;
    this.gateGain = gateGain;
    this.shapeGain = shapeGain;
    this.smoothedOutputGain = smoothedOutputGain;
  }
}

class TransientShaperGateModuleInstance implements DspModuleInstance {
  private readonly processor: TransientShaperGateProcessor;

  constructor(sampleRate: number, private readonly maxBlockSize: number) {
    this.processor = new TransientShaperGateProcessor({ sampleRate });
  }

  reset(): void { this.processor.reset(); }
  setParameter(id: string, value: number): void { this.processor.setParameter(id, value); }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
    const input = block.inputs.in;
    const output = block.outputs.out;
    const transient = block.outputs['transient-envelope'];
    const sustain = block.outputs['sustain-envelope'];
    const gate = block.outputs.gate;
    if (!input || input.length < block.frames) throw new RangeError('in buffer is missing or too short');
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    if (!transient || transient.length < block.frames) throw new RangeError('transient-envelope buffer is missing or too short');
    if (!sustain || sustain.length < block.frames) throw new RangeError('sustain-envelope buffer is missing or too short');
    if (!gate || gate.length < block.frames) throw new RangeError('gate buffer is missing or too short');
    this.processor.processInto(input, output, transient, sustain, gate, block.frames);
  }
}

export const mod024 = defineDspModule({
  descriptor: {
    id: 'mir.mod-024',
    name: 'Transient shaper and hysteretic gate',
    version: '0.1.0',
    inputs: [{ id: 'in', kind: 'audio', channels: 1 }],
    outputs: [
      { id: 'out', kind: 'audio', channels: 1 },
      { id: 'transient-envelope', kind: 'control', channels: 1 },
      { id: 'sustain-envelope', kind: 'control', channels: 1 },
      { id: 'gate', kind: 'control', channels: 1 },
    ],
    parameters: [
      { id: 'fast-attack-ms', defaultValue: 0.5, minValue: 0, maxValue: 20, unit: 'ms' },
      { id: 'fast-release-ms', defaultValue: 30, minValue: 1, maxValue: 200, unit: 'ms' },
      { id: 'slow-attack-ms', defaultValue: 20, minValue: 1, maxValue: 200, unit: 'ms' },
      { id: 'slow-release-ms', defaultValue: 180, minValue: 10, maxValue: 2_000, unit: 'ms' },
      { id: 'attack-gain-db', defaultValue: 6, minValue: -24, maxValue: 24, unit: 'dB' },
      { id: 'sustain-gain-db', defaultValue: 0, minValue: -24, maxValue: 24, unit: 'dB' },
      { id: 'gate-open-db', defaultValue: -48, minValue: -80, maxValue: 0, unit: 'dBFS' },
      { id: 'gate-hysteresis-db', defaultValue: 6, minValue: 0, maxValue: 24, unit: 'dB' },
      { id: 'gate-attack-ms', defaultValue: 3, minValue: 0, maxValue: 100, unit: 'ms' },
      { id: 'gate-release-ms', defaultValue: 80, minValue: 0, maxValue: 2_000, unit: 'ms' },
      { id: 'output-gain-db', defaultValue: 0, minValue: -24, maxValue: 12, unit: 'dB' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate <= 0
      || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new TransientShaperGateModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
